<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? $t("actions.add") + " Tipo" : "Editar Tipo" }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Origen (nivel 1): visible solo en Siniestro; en Incidente es fijo y no se muestra -->
          <b-form-group v-if="withOrigin" label="Origen" label-for="origin">
            <v-select
              v-if="isAdd"
              id="origin"
              :value="itemData.root.code"
              :options="originOptions"
              :reduce="(o) => o.code"
              label="name"
              :clearable="false"
              @input="onOriginChange"
            />
            <b-form-input v-else id="origin" :value="itemData.root.name" disabled />
          </b-form-group>

          <!-- ── TIPO (child) ── -->
          <!-- <label class="font-weight-bold">Tipo</label> -->

          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group label="Nombre Tipo" label-for="name">
              <b-form-input
                id="name"
                v-model="itemData.child.name"
                :state="getValidationState(validationContext)"
                trim
                :placeholder="withOrigin ? 'Ej. Accidental' : 'Ej. Flujo de video'"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <validation-provider
            #default="validationContext"
            name="code"
            rules="required"
          >
            <b-form-group label="Código Tipo" label-for="code">
              <b-form-input
                id="code"
                v-model="itemData.child.code"
                :state="getValidationState(validationContext)"
                trim
                :placeholder="withOrigin ? 'Ej. ACCIDENTAL' : 'Ej. FLUJO_DE_VIDEO'"
              />
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              class="text-capitalize"
              @click="hide"
            >
              {{ $t("actions.cancel") }}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { ref } from "@vue/composition-api";
import { required } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import vSelect from "vue-select";
import { originsFor, showOrigin, isOrigin } from "@/views/helpdesk/catalogConfig";
import { useUtils as useI18nUtils } from "@core/libs/i18n";


export default {
  components: {
    vSelect,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    catalog: {
      type: String,
      default: "siniestro",
    },
  },
  data() {
    return {
      required,
    };
  },
  setup(props, { emit }) {
    const { t } = useI18nUtils();
    const trans = {
      "messages.edit_save.successfully_created": t(
        "messages.edit_save.successfully_created"
      ),
      "messages.edit_save.updated_successfully": t(
        "messages.edit_save.updated_successfully"
      ),
    };

    // Origen (nivel 1): por defecto el primero del catálogo (HUMANO)
    const originOptions = originsFor(props.catalog);
    const withOrigin = showOrigin(props.catalog);
    const blankData = {
      root: { ...originOptions[0] },
      child: { code: "", name: "" }, // Tipo (nivel 2)
    };

    const onOriginChange = (code) => {
      const origin = originOptions.find((o) => o.code === code);
      if (origin) itemData.value.root = { ...origin };
    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const rootTree = ref(null); // árbol de la raíz al editar (con children_recursive)
    const editingId = ref(null); // id del subtipo en edición

    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      rootTree.value = null;
      editingId.value = null;
    };

    // ── ALTA (POST) ── raíz sin id + subtipo con children: []
    const buildAddPayload = () => ({
      catalog: props.catalog,
      code: itemData.value.root.code,
      name: itemData.value.root.name,
      children: [
        {
          code: itemData.value.child.code,
          name: itemData.value.child.name,
          children: [],
        },
      ],
    });

    // ── Helpers de árbol (PUT) ──
    const toPutNode = (node, isRoot) => {
      const out = {};
      if (node.id != null) out.id = node.id;
      if (isRoot) out.catalog = node.catalog || props.catalog;
      out.code = node.code;
      out.name = node.name;
      const kids = node.children_recursive || node.children || [];
      out.children = kids.map((k) => toPutNode(k, false)); // siempre array
      return out;
    };

    const setCodeName = (nodes, targetId, code, name) => {
      for (const n of nodes || []) {
        if (n.id === targetId) {
          n.code = code;
          n.name = name;
          return true;
        }
        const kids = n.children_recursive || n.children || [];
        if (kids.length && setCodeName(kids, targetId, code, name)) return true;
      }
      return false;
    };

    const onSubmit = async () => {
      let dispatch;

      if (props.isAdd) {
        const child = {
          code: itemData.value.child.code,
          name: itemData.value.child.name,
        };

        // Traemos el árbol fresco para saber si el Tipo ya existe.
        try {
          await store.dispatch("helpdesk/getAllFailureCategories", { catalog: [props.catalog] });
        } catch (e) {
          // seguimos con lo que haya
        }

        const roots =
          (store.state.helpdesk && store.state.helpdesk.items) || [];
        const existingRoot = roots.find(
          (r) => r.catalog === props.catalog && isOrigin(r, itemData.value.root)
        );

        if (existingRoot) {
          // El Tipo ya existe → agregamos el Subtipo con PUT (conservando hermanos).
          const root = JSON.parse(JSON.stringify(existingRoot));
          const kids = root.children_recursive || root.children || [];
          kids.push({ code: child.code, name: child.name, children: [] });
          root.children_recursive = kids;

          dispatch = store.dispatch("helpdesk/editFailureCategories", {
            id: root.id,
            params: toPutNode(root, true),
          });
        } else {
          // Tipo nuevo → POST.
          dispatch = store.dispatch(
            "helpdesk/addFailureCategories",
            buildAddPayload()
          );
        }
      } else {
        // Edición de un Subtipo existente
        if (!rootTree.value || !editingId.value) {
          toast({
            component: ToastificationContent,
            props: {
              title: "No se pudo identificar el registro a editar.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
          return;
        }
        const root = JSON.parse(JSON.stringify(rootTree.value));
        setCodeName(
          root.children_recursive || root.children || [],
          editingId.value,
          itemData.value.child.code,
          itemData.value.child.name
        );
        dispatch = store.dispatch("helpdesk/editFailureCategories", {
          id: root.id,
          params: toPutNode(root, true),
        });
      }

      dispatch
        .then((response) => {
          if (response && response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: props.isAdd
                  ? trans["messages.edit_save.successfully_created"]
                  : trans["messages.edit_save.updated_successfully"],
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: (response && response.message) || "No se pudo guardar.",
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((error) => {
          toast({
            component: ToastificationContent,
            props: {
              title: error?.response?.data?.message || "Error al guardar.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      if (!props.isAdd && props.itemEdit) {
        const edit = JSON.parse(JSON.stringify(props.itemEdit));
        rootTree.value = edit.__root || null;
        editingId.value = edit.id;
        itemData.value = {
          root: {
            code: rootTree.value?.code || originOptions[0].code,
            name: rootTree.value?.name || originOptions[0].name,
          },
          child: {
            code: edit.code || "",
            name: edit.name || "",
          },
        };
      }
    };

    return {
      itemData,
      originOptions,
      withOrigin,
      onOriginChange,
      onSubmit,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
    };
  },
};
</script>